import { createAsyncThunk } from "@reduxjs/toolkit";
import { createFormSlice } from "../../../shared/slice";
import {
    fetchContactById,
    fetchDepartmentById,
    transformDraftItems,
} from "../../shared/slice/entities";
import { fetchSalesInvoiceDraft } from "../../../../../../../../../services/dataEntering/salesInvoiceDraft/api";
import { SalesInvoiceDraft } from "../../../../../../../../../services/dataEntering/salesInvoiceDraft/types";
import { InvoiceFormState, SalesInvoiceTypeEnum } from "./types";
import { AppDispatch, GetRootState } from "../../../../../../../../../shared/redux/types";

const toFormState = async (
    draft: SalesInvoiceDraft,
    accountId: number,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<InvoiceFormState> => {
    const [buyer, department, items] = await Promise.all([
        fetchContactById(draft.buyerId, accountId, dispatch, getState),
        fetchDepartmentById(draft.departmentId, accountId, dispatch, getState),
        transformDraftItems(draft.items, accountId, dispatch, getState),
    ]);

    return {
        buyer,
        publicId: draft.publicId ?? "",
        taxPaymentDate: draft.taxPaymentDate,
        dueDate: draft.dueDate,
        issueDate: draft.issueDate,
        consumptionCountryId: draft.consumptionCountryId,
        type: draft.type as SalesInvoiceTypeEnum,
        externalId: "",
        text: draft.text ?? "",
        currencyId: draft.currencyId ?? "",
        paymentMethod: draft.paymentMethod,
        bankAccount: draft.bankAccount ?? null,
        contract: null,
        department,
        linkedDocument: draft.linkedDocument ?? null,
        variableSymbol: draft.variableSymbol ?? "",
        constantSymbol: draft.constantSymbol ?? "",
        specificSymbol: draft.specificSymbol ?? "",
        exchangeRate: draft.exchangeRate == null ? "" : String(draft.exchangeRate),
        items,
    };
};

export const setDefaults = createAsyncThunk(
    "salesInvoiceForm/setDefaults",
    async (accountId: number, { dispatch, getState, rejectWithValue }) => {
        try {
            const { documentId } = (getState as GetRootState)().dataEntering;
            const draft = await fetchSalesInvoiceDraft({
                dispatch: dispatch as AppDispatch,
                accountId,
                documentId,
            });

            return await toFormState(
                draft,
                accountId,
                dispatch as AppDispatch,
                getState as GetRootState,
            );
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

const slice = createFormSlice("salesInvoiceForm", (builder) => {
    builder
        .addCase(setDefaults.fulfilled, (draftState, action) => {
            draftState.data = action.payload;
            draftState.status = "initialized";
        })
        .addCase(setDefaults.pending, (draftState) => {
            draftState.data = {};
            draftState.status = "initializing";
        })
        .addCase(setDefaults.rejected, (draftState) => {
            draftState.data = {};
            draftState.status = "error";
        });
});

export const { name, actions, reducer } = slice;
